import * as React from 'react';
import { useMemo, useState, useEffect, useContext, useReducer, useCallback } from 'react';
import "./ChatItem.scss";
import { validationReducer } from '../../util/validationReducer';
import { FormBuilder } from '../../common/FormBuilder';
import { UserDataContext } from '../contents/Contents';
import { ValidMessageReducer, ValidateMessages } from '../validation/IValidation';
import { ChatItemDefines, ChatItemProps } from './IChatItem';
import { Props } from '../../common/types/props';
import { EventProps, UserDataProps, ActionDefines } from '../../interface/IEvent';
import useActions from '../../common/useActions';
import { EventType } from '../../common/types/event';
import { ActionType } from '../../common/enum/action';
import valueFromValid from '../../util/valueFromValid';
import { ColorDefines } from '../../interface/IColors';
import { POSITION } from '../../common/enum/position';
import { dispatchBalloonColors } from './colorManager';
import { Defines } from '../../common/types/defines';

interface ChatItem {
  props: ChatItemProps;
  defines: ChatItemDefines;
  colors: ColorDefines;
}

interface ContentProps {
  props: Props;
  defines: Defines;
  hidden: boolean;
  colors: ColorDefines;
}

const ChatItem = ({props, defines, colors}: ChatItem) => {
  const userData = useContext(UserDataContext);
  const [touched, setTouched] = useState(false);
  const [valid, dispatchValid] = useReducer<ValidMessageReducer>(validationReducer, {} as ValidateMessages);
  const { actions } = useActions(valid, props.setStep, props.index, setTouched, props.ts, props.collapseCallback);

  const onload = useCallback(() => {
    if (null == props.events) return;
    const load = props.events?.load;
    if (!load || !load.callbacks || load.callbacks.length == 0) return;
    load.callbacks.map(callback =>
      callback({ state: EventType.load, step: props.index, ts: props.ts, data: userData, def: defines }));
  }, [props.events]);

  useEffect(() => {
    if (props.hidden) return;
    onload();
  }, [props.hidden]);

  useEffect(() => {
    if (props.hidden) return;
    setTouched(true);
    props.setStep(props.index);
    const keys = Object.keys(valid);
    keys.forEach(key => {
      const cb = valid[key].setErrorCallback;
      const e = valid[key].error;
      if (cb) cb(e);
    });
  }, [valid, props.hidden]);

  const getCallbackDataProps = useCallback(() => {
    // console.debug('ChatItem getCallbackDataProps', userData)
    return {
      step: props.index,
      ts: props.ts,
      value: valueFromValid(valid, null),
      data: userData
    }
  }, [props.index, props.ts, valid, userData]);

  const getEventsByProps = useCallback((key: EventType) => {
    return props?.events ? props?.events[key] : null;
  }, [props?.events]);

  const getActionsByActionDefines = useCallback((def?: ActionDefines) => {
    return (def?.actions || []).map(a => actions[a]);
  }, [actions]);

  const getCallbacksByActionDefines = useCallback((def?: ActionDefines) => {
    return def?.callbacks || []
  }, [])

  const createContentsEvent = useCallback((def: Defines, visible: boolean) => {
    // console.debug('ChatItem formContents createContentsEvent')
    return Object.keys(EventType).reduce<EventProps>((p, _key) => {
      const key = Object.keys(EventType).find(e => e == _key) as EventType;
      const propsEvent = getEventsByProps(key);
      const definesEvent = def?.event ? def?.event[key] : null;

      const callbackData: UserDataProps = {
        step: props.index,
        ts: props.ts,
        value: visible ? valueFromValid(valid, null) : undefined,
        data: { ...userData },
      };
      p[key] = {
        callbacks: [
          ...getActionsByActionDefines(propsEvent),
          ...getCallbacksByActionDefines(propsEvent),
          ...getCallbacksByActionDefines(definesEvent),
          ...getActionsByActionDefines(definesEvent),
        ],
        data: callbackData
      };
      return p;
    }, {});
  }, [
    props?.events,
    props?.events ? Object.keys(props?.events) : undefined,
    touched,
    props.index,
    props.ts,
    userData
  ]);
  
  const formContents = useMemo(() => {
    if (!defines?.contents || !Array.isArray(defines.contents) || defines.contents.length == 0) return [];

    const visible = !props.hidden;

    const contents = defines.contents.map<ContentProps>(def => {
      // console.debug('ChatItem formContents', def)
      return {
        defines: def,
        hidden: props.hidden,
        props: {
          visible,
          values: visible ? valueFromValid(valid, null) : undefined,
          parentValues: visible ? valueFromValid(valid, null) : undefined,
          valid: (valid as any)[def.name],
          value: valid[def.name]?.value,
          enable: touched && Object.keys(valid).every(key => valid[key].error && valid[key].error.length == 0),
          error: valid[def.name]?.error || [],
          dispatchValid,
          event: def.event ? createContentsEvent(def, visible) : undefined,
          state: props.progressState,
          getData: getCallbackDataProps,
          option: props.option
        },
        colors
      };
    });
    return FormBuilder(contents);
  }, [
    touched,
    valid,
    valid[defines.name]?.value,
    valid[defines.name]?.error,
    // Object.keys(valid),
    defines,
    defines.name,
    defines?.contents,
    defines?.event,
    props.hidden,
    props.progressState,
    props.option,
    colors
  ]);
  
  const balloonColors = useMemo(() => dispatchBalloonColors(colors), [colors?.balloon?.left, colors?.balloon?.right, colors?.balloon?.center]);
  
  if (props.hidden) return <></>;

  
  return (
    <div className={"chat" + (!props.hidden ? " show" : "") + (props.person.position ? " " + props.person.position : "")}>
      {props.person?.icon && (
        <div
          className="icon"
          style={{
            backgroundImage: `url(${props.person.icon.url})`,
            backgroundColor: balloonColors.icon?.primary || "initial",
            borderColor: balloonColors.icon?.secondary || "transparent",
            borderStyle: balloonColors.icon?.secondary && "solid",
            borderWidth: balloonColors.icon?.secondary && 3,
            color: balloonColors.icon?.font || "initial"
          }}
        ></div>
      )}
      {props.person.position &&
        <div className={"item " + props.person.position + (props.person?.grow ? " grow" : "")}
          style={{
            color: balloonColors[props.person.position]?.font,
          }}>
          {props.person?.icon && <div className="arrow"
            style={{
              borderLeftColor: props.person.position === POSITION.RIGHT ? balloonColors[props.person.position]?.primary : "transparent",
              borderRightColor: props.person.position === POSITION.LEFT ? balloonColors[props.person.position]?.primary : "transparent",
            }}></div>}
          <div className="container"
              style={{
                backgroundColor: balloonColors[props.person.position]?.primary,
              }}>
            {defines.label && <div className="name">{defines.label}</div>}
            <form
              className="balloon"
            >
              {formContents}
            </form>
          </div>
        </div>
      }
    </div>
  )
}

export default ChatItem;